import { useGetPlatformApiKey } from '@/api/looker/session/use-get-platform-api-key';
import { appConfig } from '@/config';
import { useLookerModelId } from '@/hooks/looker/use-looker-model-id';
import { useAuth } from '@/hooks/use-auth';

type Param = string | number | boolean | undefined;

export type DexFetchClientParams = {
  path: string;
  params?: Record<string, Param | Param[]>;
  signal?: AbortSignal;
};

export function useDexFetchClient() {
  const auth = useAuth();
  const getPlatformApiKey = useGetPlatformApiKey();
  const model = useLookerModelId();

  return async function dexFetchClient<D>({
    path,
    params = {},
    signal,
  }: DexFetchClientParams): Promise<D> {
    const platformApiKey = await getPlatformApiKey(model);

    const url = new URL(`${appConfig.bffUrl}/dex${path}`);

    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'undefined') {
        continue;
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'undefined') {
            continue;
          }

          url.searchParams.append(key, item.toString());
        }
        continue;
      }

      url.searchParams.set(key, value.toString());
    }

    const accessToken = await auth.getAccessToken();

    const response = await fetch(url, {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-ACCURE-API-KEY': platformApiKey,
        authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = new Error(
        `Network response was not ok: ${response.status} ${response.statusText}`,
      ) as Error & { status: number; statusText: string };
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }

    const data = await response.json();

    return data as D;
  };
}
